// Traduit le statut d'une demande en « où en est-on » lisible pour la fiche :
// une position dans la frise de progression + un bandeau (qui doit agir, en
// langage clair). Pur affichage, dérivé de la machine à états
// (server/utils/statuts.ts) — ne décide RIEN, ne fait que reformuler.
import type { Statut } from '../../types';
import { dePersonne } from '../messages/format';

// Les 7 phases du parcours normal. La recherche d'un·e remplaçant·e (interne 1/2,
// externe, fusion, évaluation) est regroupée sous « Recherche » — sous-étapes
// internes. « Retour » : après la séance, la personne qui a remplacé transmet son
// retour (faits marquants, protocoles, liste de présence) à la personne remplacée.
export const PHASES_PARCOURS = ['Recherche', 'Confirmation', 'Validations', 'Tipee', 'Passation', 'Atelier', 'Retour'] as const;

// Phase courante (1..7). 8 = tout est fait (clôturée). null = hors parcours
// normal (urgence, ou fin anticipée : absence validée, fusionné, annulé…) →
// pas de frise, seulement le bandeau d'issue. « Atelier » (6) devient « Retour » (7)
// dès que la séance est passée : voir phaseCourante.
const PHASE_PAR_STATUT: Record<Statut, number | null> = {
  brouillon: 1,
  soumise: 1,
  recherche_interne_1: 1,
  recherche_interne_2: 1,
  recherche_externe: 1,
  fusion_demandee: 1,
  evaluation_annulation: 1,
  attente_confirmation_remplacant: 2,
  validation_responsable: 3,
  validation_rh: 3,
  attente_tipee: 4,
  passation: 5,
  atelier_donne: 6,
  cloturee: 8,
  urgence_cascade: null,
  absence_validee: null,
  fusionnee: null,
  annulee: null,
  maintenue: null,
  refusee: null,
  sans_suite: null,
};

/** La séance est-elle passée ? (strictement avant aujourd'hui : le retour s'attend « le lendemain matin ».) */
export function seancePasseeLe(dateAtelier: string, aujourdhui: Date = new Date()): boolean {
  const jour = `${aujourdhui.getFullYear()}-${String(aujourdhui.getMonth() + 1).padStart(2, '0')}-${String(aujourdhui.getDate()).padStart(2, '0')}`;
  return dateAtelier.slice(0, 10) < jour;
}

/**
 * Phase courante. Sans changer la machine à états : « atelier_donne » est l'« Atelier » (6) tant que la séance n'a pas eu
 * lieu, puis le « Retour » (7) — la personne qui a remplacé transmet son retour, la gestion contrôle la liste de présence.
 */
export function phaseCourante(statut: Statut, dateAtelier?: string | null, aujourdhui: Date = new Date()): number | null {
  if (statut === 'atelier_donne' && dateAtelier && seancePasseeLe(dateAtelier, aujourdhui)) return 7;
  return PHASE_PAR_STATUT[statut];
}

// Qui tient la balle à cette étape — pilote la couleur et l'icône du bandeau.
export type ActeurParcours = 'gestion' | 'remplacant' | 'absent' | 'termine' | 'alerte';

export interface InfoBandeau {
  acteur: ActeurParcours;
  phaseLabel: string; // petit sur-titre : NOM DE LA PHASE seul. Le « qui agit »
                      // est ajouté par DemandePage, adapté à la personne qui
                      // regarde (« à vous de jouer » vs « suivi par l'équipe gestion »).
  titre: string;      // phrase principale, en clair
  sousTitre?: string; // contexte / prochaine étape
}

const INFO_BANDEAU: Record<Statut, InfoBandeau> = {
  brouillon: { acteur: 'gestion', phaseLabel: 'Brouillon', titre: 'Demande créée, pas encore soumise.', sousTitre: 'À soumettre pour lancer le traitement.' },
  soumise: { acteur: 'gestion', phaseLabel: 'Recherche', titre: 'Soumise — recherche d’un·e remplaçant·e à lancer.' },
  recherche_interne_1: { acteur: 'gestion', phaseLabel: 'Recherche', titre: 'Recherche interne en cours — 1er appel Slack.', sousTitre: 'En attente de réponses au 1er appel.' },
  recherche_interne_2: { acteur: 'gestion', phaseLabel: 'Recherche', titre: 'Recherche interne — relance envoyée.', sousTitre: 'Sans réponse, la recherche peut passer au vivier externe.' },
  recherche_externe: { acteur: 'gestion', phaseLabel: 'Recherche', titre: 'Recherche élargie au vivier externe.' },
  fusion_demandee: { acteur: 'gestion', phaseLabel: 'Recherche', titre: 'Fusion avec un autre atelier à l’étude.' },
  evaluation_annulation: { acteur: 'gestion', phaseLabel: 'Recherche', titre: 'À évaluer : annuler ou maintenir la séance ?' },
  attente_confirmation_remplacant: { acteur: 'remplacant', phaseLabel: 'Confirmation', titre: 'En attente de confirmation du/de la remplaçant·e.' },
  validation_responsable: { acteur: 'gestion', phaseLabel: 'Validations', titre: 'En attente de validation du/de la responsable.', sousTitre: 'Remplaçant·e confirmé·e. Prochaine étape : validation RH.' },
  validation_rh: { acteur: 'gestion', phaseLabel: 'Validations', titre: 'En attente de validation des RH.', sousTitre: 'Contrôle des dates et de l’attribution.' },
  attente_tipee: { acteur: 'gestion', phaseLabel: 'Validation RH (Tipee)', titre: 'En attente de la validation de l’absence par les RH dans Tipee — la passation s’ouvrira d’elle-même.' },
  // La transmission (FRA, contacts) se fait entre l'absent·e et le/la
  // remplaçant·e hors app ; mais dans l'app, seule la gestion coche la
  // checklist (onglet Passation, boutons visibles uniquement peutGerer) —
  // acteur 'gestion' pour que le bandeau pointe vers qui a un vrai bouton ici.
  passation: { acteur: 'gestion', phaseLabel: 'Passation', titre: 'Passation en cours.', sousTitre: "L'intervenant·e absent·e transmet les infos utiles (FRA, contacts) ; la gestion coche la checklist ci-dessous." },
  atelier_donne: { acteur: 'gestion', phaseLabel: 'Après l’atelier', titre: 'Atelier assuré — présences à contrôler.', sousTitre: 'Dernière étape avant clôture.' },
  cloturee: { acteur: 'termine', phaseLabel: 'Terminé', titre: 'Remplacement effectué et clôturé.' },
  absence_validee: { acteur: 'termine', phaseLabel: 'Terminé', titre: 'Absence validée — aucun remplacement nécessaire.' },
  fusionnee: { acteur: 'termine', phaseLabel: 'Terminé', titre: 'Atelier fusionné avec un autre.' },
  maintenue: { acteur: 'termine', phaseLabel: 'Terminé', titre: 'Atelier maintenu.' },
  annulee: { acteur: 'alerte', phaseLabel: 'Terminé', titre: 'Atelier annulé.' },
  refusee: { acteur: 'alerte', phaseLabel: 'Terminé', titre: 'Demande refusée / classée.' },
  sans_suite: { acteur: 'termine', phaseLabel: 'Sans suite', titre: 'Brouillon classé sans suite (aucun atelier impacté).' },
  urgence_cascade: { acteur: 'alerte', phaseLabel: 'Urgence', titre: 'Urgence — recherche en cascade en cours.', sousTitre: "L'équipe gestion appelle les remplaçant·e·s possibles, un·e par un·e." },
};

export function infoBandeau(statut: Statut, dateAtelier?: string | null, aujourdhui: Date = new Date()): InfoBandeau {
  // Atelier donné : avant la séance, c'est la passation qui est terminée ; après, le retour et les présences.
  if (statut === 'atelier_donne' && dateAtelier) {
    return seancePasseeLe(dateAtelier, aujourdhui)
      ? { acteur: 'gestion', phaseLabel: 'Retour', titre: 'Séance passée : retour et liste de présence à recevoir.',
          sousTitre: "La personne qui a remplacé transmet son retour ; la gestion contrôle la liste de présence, puis clôture." }
      : { acteur: 'gestion', phaseLabel: 'Atelier', titre: 'Passation terminée : la séance approche.',
          sousTitre: 'Après la séance, la personne qui remplace transmet son retour et la liste de présence.' };
  }
  return INFO_BANDEAU[statut];
}


// ── Carte « Prochaine étape » : textes selon QUI regarde ────────────────────
// Règle métier (2026-10-08) : l'intervenant·e absent·e cherche son/sa
// remplaçant·e en premier (ses contacts + les tours Slack) ; la gestion prend
// le relais après le 2e tour, ou plus tôt. Les textes par défaut (INFO_BANDEAU)
// sont écrits « côté gestion » : ici on les remplace pour la personne absente
// (jamais de mention des externes / du vivier, voir regle-recherche-remplacant)
// et on nomme la personne absente pour la gestion.

export interface ContexteCarte {
  peutGerer: boolean;
  estAbsent: boolean;            // la personne qui regarde EST l'absent·e
  absentNom: string;
  relanceDisponible: boolean;    // « relancer_slack » est proposé à cette personne
  relancePossibleLe: string | null; // texte déjà formaté (« jeudi 15 octobre à 10 h »)
  source?: string;               // provenance de la demande ('tipee' = détectée automatiquement)
  reserveGestion?: boolean;      // maladie à temps partiel repérée dans Tipee : la gestion décide seule
  /** La personne qui regarde détient l'action de validation de cette étape (responsable, puis RH) : elle a un vrai bouton. */
  peutValider?: boolean;
  remplacantNom?: string | null; // le/la remplaçant·e désigné·e (pour la phrase de validation)
  /** Le message Slack de la recherche en cours, pour que la carte dise ce qui s'est VRAIMENT passé (docs/DECISIONS.md §24). */
  messageSlack?: ContexteMessageSlack | null;
}

export interface ContexteMessageSlack {
  messageId: number;
  texte: string;                 // le message, pour « Copier le message »
  etat: 'a_envoyer' | 'envoi_en_cours' | 'envoye' | 'erreur' | 'plus_utile';
  relance: boolean;              // message de relance (2e tour) plutôt que 1er appel
  aCharge: 'intervenant' | 'gestion' | null;
  peutMarquer: boolean;          // la personne qui regarde peut dire « c'est publié »
  lanceParNom?: string | null;   // qui a lancé la recherche (et doit le publier)
  quand?: string | null;         // « lundi 19 octobre à 10 h 02 » : quand il est parti
}

export interface SurchargeCarte {
  acteur: ActeurParcours;
  quiAgit: string;
  titre: string;
  sousTitre?: string;
  /** Action à mettre en avant (bouton accent) ; les autres passent en neutre. */
  actionPrincipale?: string;
  /** Aucune action mise en avant : l'étape attendue n'est pas celle de la personne
   *  qui regarde (ex. gestion devant un brouillon que l'intervenant·e doit envoyer). */
  toutNeutre?: boolean;
  /** Libellés de boutons propres à cette carte (ex. « Envoyer à sa place »). */
  libellesActions?: Record<string, string>;
  /** Message Slack à publier à la main : « Copier le message » puis « C'est publié » (mis en avant sur la carte). */
  messageAction?: { messageId: number; texte: string; libelleMarquer: string };
}

/**
 * Texte de la carte, adapté à QUI regarde et à l'état du message Slack de la recherche (docs/DECISIONS.md §24) : « prêt, publiez-le »,
 * « publié », « n'a pas pu être publié ». Sans message connu, les textes d'origine (surchargeCarteBase) s'appliquent.
 */
export function surchargeCarte(statut: Statut, c: ContexteCarte): SurchargeCarte | null {
  const base = surchargeCarteBase(statut, c);
  const m = c.messageSlack;
  if (!base || !m || (statut !== 'recherche_interne_1' && statut !== 'recherche_interne_2')) return base;
  const messageAction = (m.etat === 'a_envoyer' || m.etat === 'erreur') && m.peutMarquer
    ? { messageId: m.messageId, texte: m.texte, libelleMarquer: !c.peutGerer && c.estAbsent ? "C'est publié" : 'Marquer comme publié' }
    : undefined;

  // La personne absente (sans rôle de gestion) : sa propre recherche.
  if (!c.peutGerer && c.estAbsent) {
    switch (m.etat) {
      case 'a_envoyer':
      case 'erreur':
        if (m.peutMarquer) {
          return { ...base, acteur: 'absent', quiAgit: 'À vous de jouer', actionPrincipale: undefined, messageAction,
            titre: m.etat === 'erreur'
              ? (m.relance ? "Votre relance n'a pas pu être publiée sur Slack" : "Votre message n'a pas pu être publié sur Slack")
              : (m.relance ? 'Votre relance est prête : publiez-la sur Slack' : 'Votre message est prêt : publiez-le sur Slack'),
            sousTitre: m.etat === 'erreur'
              ? `L'équipe gestion est prévenue. Vous pouvez aussi ${m.relance ? 'la' : 'le'} publier vous-même.`
              : m.relance
                ? "Copiez-la dans le canal #remplacements, puis indiquez ici qu'elle est publiée. Votre demande est déjà visible de vos collègues dans l'app."
                : "Copiez-le dans le canal #remplacements, puis indiquez ici qu'il est publié. Votre demande est déjà visible de vos collègues dans l'app." };
        }
        return { ...base, acteur: 'gestion', quiAgit: "Suivi par l'équipe gestion",
          titre: m.relance ? "Votre relance sera publiée par l'équipe gestion" : "Votre message sera publié par l'équipe gestion",
          sousTitre: 'Continuez aussi à chercher de votre côté.' };
      case 'envoi_en_cours':
        return { ...base, titre: m.relance ? 'Votre relance part sur Slack…' : 'Votre message part sur Slack…', sousTitre: 'Cela ne prend que quelques secondes.' };
      case 'envoye':
        return { ...base,
          titre: base.titre === 'Vos collègues sont prévenu·e·s' ? 'Vos collègues sont prévenu·e·s sur Slack'
            : base.titre === 'Relance envoyée à vos collègues' ? 'Relance publiée sur Slack' : base.titre,
          sousTitre: m.quand ? `${m.relance ? 'Publiée' : 'Publié'} le ${m.quand}. ${base.sousTitre ?? ''}`.trim() : base.sousTitre };
      default:
        return base;
    }
  }

  // La gestion : elle voit si le message est vraiment parti, et peut le publier à la place de la personne.
  if (c.peutGerer && (m.etat === 'a_envoyer' || m.etat === 'erreur')) {
    const sujet = m.relance ? 'La relance Slack' : 'Le 1er appel Slack';   // féminin / masculin : l'accord suit
    const e = m.relance ? 'e' : '';
    const parIntervenant = m.aCharge === 'intervenant';
    const qui = m.lanceParNom ?? c.absentNom;
    return { ...base, acteur: 'gestion', messageAction,
      quiAgit: parIntervenant ? `Menée par ${c.absentNom}` : 'À vous de jouer',
      titre: m.etat === 'erreur' ? `${sujet} ${dePersonne(c.absentNom)} n'a pas pu être publié${e}` : `${sujet} ${dePersonne(c.absentNom)} n'est pas encore publié${e}`,
      sousTitre: parIntervenant
        ? `${qui} doit ${m.relance ? 'la' : 'le'} publier dans #remplacements. Vous pouvez ${m.relance ? 'la' : 'le'} publier à sa place.`
        : `À publier par ${qui} dans #remplacements.` };
  }
  return base;
}

function surchargeCarteBase(statut: Statut, c: ContexteCarte): SurchargeCarte | null {
  if (!c.peutGerer && c.estAbsent) {
    switch (statut) {
      case 'brouillon':
        return { acteur: 'absent', quiAgit: 'À vous de jouer', titre: "Votre demande de remplacement n'est pas encore envoyée",
          sousTitre: 'Envoyez-la pour lancer la recherche de remplaçant·e.', actionPrincipale: 'soumettre' };
      case 'soumise':
        return { acteur: 'absent', quiAgit: 'À vous de jouer', titre: 'Trouvez votre remplaçant·e',
          sousTitre: "Contactez directement un·e collègue, ou prévenez toute l'équipe sur Slack. Dès que quelqu'un accepte, indiquez-le ici.",
          actionPrincipale: 'lancer_recherche' };
      case 'recherche_interne_1':
        if (c.relanceDisponible) {
          return { acteur: 'absent', quiAgit: 'À vous de jouer', titre: 'Pas encore de réponse ? Relancez vos collègues',
            sousTitre: 'Une relance sur Slack augmente vos chances de trouver quelqu\'un. Continuez aussi à chercher de votre côté.',
            actionPrincipale: 'relancer_slack' };
        }
        return { acteur: 'absent', quiAgit: 'À vous de jouer', titre: 'Vos collègues sont prévenu·e·s',
          sousTitre: `Continuez aussi à chercher de votre côté.${c.relancePossibleLe ? ` Vous pourrez relancer vos collègues dès le ${c.relancePossibleLe}.` : ''}`,
          actionPrincipale: 'saisir_remplacant' };
      case 'recherche_interne_2':
        return { acteur: 'absent', quiAgit: 'À vous de jouer', titre: 'Relance envoyée à vos collègues',
          sousTitre: "Continuez à chercher de votre côté. Sans réponse, l'équipe gestion viendra vous aider.",
          actionPrincipale: 'saisir_remplacant' };
      case 'recherche_externe':
        return { acteur: 'absent', quiAgit: "À vous de jouer, avec l'équipe gestion",
          titre: 'Continuez à chercher : votre réseau reste le plus rapide',
          sousTitre: "L'équipe gestion vous aide maintenant et explore d'autres solutions. Votre aide compte toujours : un message direct à un·e collègue aboutit souvent plus vite. Dès que vous trouvez quelqu'un, indiquez-le ici.",
          actionPrincipale: 'saisir_remplacant' };
      case 'fusion_demandee':
      case 'evaluation_annulation':
        return { acteur: 'gestion', quiAgit: "Suivi par l'équipe gestion",
          titre: "L'équipe gestion étudie d'autres solutions pour votre atelier",
          sousTitre: 'Vous serez prévenu·e de la décision.' };
      case 'attente_tipee':
        return { acteur: 'gestion', quiAgit: 'Suivi par les RH', titre: 'Les RH valident votre absence dans Tipee',
          sousTitre: "Votre remplacement est validé. Vous avez déjà fait votre demande dans Tipee : vous n'avez plus rien à faire." };
      default:
        return null;
    }
  }
  if (c.peutGerer) {
    switch (statut) {
      case 'brouillon':
        // La gestion qui est elle-même l'absente : même carte que l'intervenant·e.
        if (c.estAbsent) {
          return { acteur: 'absent', quiAgit: 'À vous de jouer', titre: "Votre demande de remplacement n'est pas encore envoyée",
            sousTitre: 'Envoyez-la pour lancer la recherche de remplaçant·e.', actionPrincipale: 'soumettre' };
        }
        // Maladie à temps partiel : l'intervenant·e n'est pas prévenu·e, c'est à la
        // gestion de trancher (envoyer à sa place, ou écarter).
        if (c.reserveGestion) {
          return { acteur: 'gestion', quiAgit: 'À vous de jouer',
            titre: 'Maladie à temps partiel : à vous de décider',
            sousTitre: `${c.absentNom} n'a pas été prévenu·e. Envoyez la demande à sa place pour lancer la recherche de remplaçant·e, ou écartez-la si aucun remplacement n'est nécessaire.`,
            libellesActions: { soumettre: 'Envoyer à sa place' } };
        }
        // Un brouillon est à compléter/envoyer par l'intervenant·e : la gestion n'agit
        // qu'en recours (écarter, annuler, urgence, ou envoyer à sa place).
        return { acteur: 'absent', quiAgit: `En attente ${dePersonne(c.absentNom)}`,
          titre: 'Demande de remplacement pas encore envoyée',
          sousTitre: c.source === 'tipee'
            ? `Une absence ${dePersonne(c.absentNom)} a été repérée dans Tipee sur cet atelier. La demande de remplacement est à compléter et à envoyer pour lancer la recherche. Vous pouvez l'envoyer à sa place si besoin.`
            : `${c.absentNom} a commencé sa demande sans l'envoyer. Vous pouvez l'envoyer à sa place si besoin.`,
          toutNeutre: true, libellesActions: { soumettre: 'Envoyer à sa place' } };
      case 'soumise':
        return { acteur: 'gestion', quiAgit: `Menée par ${c.absentNom}`, titre: `${c.absentNom} cherche un·e remplaçant·e`,
          sousTitre: `${c.absentNom} n'a pas encore prévenu ses collègues sur Slack. Vous pouvez prendre le relais à tout moment.` };
      case 'recherche_interne_1':
        return { acteur: 'gestion', quiAgit: `Menée par ${c.absentNom}`, titre: `${c.absentNom} attend des réponses au 1er appel Slack`,
          sousTitre: `${c.absentNom} peut relancer ses collègues après quelques jours. Vous pouvez prendre le relais à tout moment.` };
      case 'recherche_interne_2':
        return { acteur: 'gestion', quiAgit: `Menée par ${c.absentNom}`, titre: 'Second tour Slack en cours',
          sousTitre: 'Sans réponse à la fin du tour, vous prenez le relais (recherche élargie, fusion…). Vous pouvez intervenir avant.' };
      case 'validation_responsable':
        // C'est à cette personne de valider : on le lui dit, au lieu d'une phrase écrite pour « la gestion » en général.
        if (c.peutValider) {
          return { acteur: 'gestion', quiAgit: 'À vous de jouer', titre: 'Cette demande attend votre validation',
            sousTitre: `${c.remplacantNom ?? 'Un·e remplaçant·e'} remplacerait ${c.absentNom} et a confirmé. Vérifiez ce remplacement, puis validez : les RH contrôleront ensuite.`,
            actionPrincipale: 'valider_responsable' };
        }
        return null;
      case 'validation_rh':
        if (c.peutValider) {
          return { acteur: 'gestion', quiAgit: 'À vous de jouer', titre: 'Cette demande attend votre validation',
            sousTitre: `Contrôlez les dates et l'attribution du remplacement ${dePersonne(c.absentNom)}, puis validez : la demande passera à la validation de l'absence dans Tipee.`,
            actionPrincipale: 'valider_rh' };
        }
        return null;
      case 'attente_tipee':
        return { acteur: 'gestion', quiAgit: 'Congé Tipee', titre: `Validez l'absence ${dePersonne(c.absentNom)} dans Tipee`,
          sousTitre: `${c.absentNom} a fait sa demande dans Tipee en déclarant son absence. Une fois validée dans Tipee, confirmez-le ici.` };
      default:
        return null;
    }
  }
  return null;
}
